import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import axios from '../config/axiosConfig';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth } from './AuthContext';
import { useTheme } from '@mui/material/styles';
import {
  CircularProgress,
  Container,
  Button,
  Box,
  Tabs,
  Tab,
} from '@mui/material';
import TitleAndInfo from './TitleAndInfo';
import { exportToCsv } from '../utils/exportToCsv';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExtendedHoursGainers from './ExtendedHoursGainers';
import ExtendedHoursLosers from './ExtendedHoursLosers';
import StockHighLowHighs from './StockHighLowHighs';
import StockHighLowLows from './StockHighLowLows';
import MarketHoursGainers from './MarketHoursGainers';
import MarketHoursLosers from './MarketHoursLosers';

// Tab Panel component for accessibility
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`market-tabpanel-${index}`}
      aria-labelledby={`market-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Helper function for a11y
function a11yProps(index) {
  return {
    id: `market-tab-${index}`,
    'aria-controls': `market-tabpanel-${index}`,
  };
}

const MarketMovers = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const baseUrl = window.location.origin;
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab mapping for URL parameters
  const TAB_MAPPING = {
    'gainers': 0,
    'losers': 1,
    'actives': 2,
    'pre-market': 3,
    'after-market': 4,
    'highs-lows': 5
  };

  const SUB_TAB_MAPPING = {
    'gainers': 0,
    'losers': 1,
    'highs': 0,
    'lows': 1
  };

  // Get initial tab value from URL parameter
  const getInitialTabValue = () => {
    const tab = searchParams.get('tab');
    return TAB_MAPPING[tab] !== undefined ? TAB_MAPPING[tab] : 0;
  };

  const getInitialSubTabValue = (paramName) => {
    const subTab = searchParams.get(paramName);
    return SUB_TAB_MAPPING[subTab] !== undefined ? SUB_TAB_MAPPING[subTab] : 0;
  };

  // State for main tab navigation
  const [tabValue, setTabValue] = useState(getInitialTabValue);

  // States for pre-market and after-market sub-tabs
  const [preMarketTab, setPreMarketTab] = useState(() => getInitialSubTabValue('premarket'));
  const [afterMarketTab, setAfterMarketTab] = useState(() => getInitialSubTabValue('aftermarket'));
  const [highLowTab, setHighLowTab] = useState(() => getInitialSubTabValue('highlow'));

  // State for data
  const [marketGainers, setMarketGainers] = useState([]);
  const [marketLosers, setMarketLosers] = useState([]);
  const [marketActives, setMarketActives] = useState([]);
  const [extendedHoursData, setExtendedHoursData] = useState({
    pre: {
      gainers: [],
      losers: []
    },
    after: {
      gainers: [],
      losers: []
    }
  });
  const [highLowData, setHighLowData] = useState({
    highs: [],
    lows: []
  });

  // Loading states for each data type
  const [loadingGainers, setLoadingGainers] = useState(true);
  const [loadingLosers, setLoadingLosers] = useState(true);
  const [loadingActives, setLoadingActives] = useState(true);
  const [loadingExtendedHours, setLoadingExtendedHours] = useState(true);
  const [loadingHighLow, setLoadingHighLow] = useState(true);

  // Update URL when tab changes
  const updateUrlParams = (mainTab, subTabType = null, subTabValue = null) => {
    const params = new URLSearchParams(searchParams);

    // Set main tab parameter
    const mainTabKey = Object.keys(TAB_MAPPING).find(key => TAB_MAPPING[key] === mainTab);
    if (mainTabKey) {
      params.set('tab', mainTabKey);
    }

    // Set sub-tab parameters
    if (subTabType && subTabValue !== null) {
      const subTabKey = Object.keys(SUB_TAB_MAPPING).find(key => SUB_TAB_MAPPING[key] === subTabValue);
      if (subTabKey) {
        params.set(subTabType, subTabKey);
      }
    }

    // Clean up unused sub-tab parameters
    if (mainTab < 3) {
      // Regular tabs - remove all sub-tab params
      params.delete('premarket');
      params.delete('aftermarket');
      params.delete('highlow');
    } else if (mainTab === 3) {
      // Pre-market tab - keep premarket param, remove others
      params.delete('aftermarket');
      params.delete('highlow');
    } else if (mainTab === 4) {
      // After-market tab - keep aftermarket param, remove others
      params.delete('premarket');
      params.delete('highlow');
    } else if (mainTab === 5) {
      // Highs/Lows tab - keep highlow param, remove others
      params.delete('premarket');
      params.delete('aftermarket');
    }

    setSearchParams(params);
  };

  // Content configuration for regular market tabs
  const tabsConfig = [
    {
      id: 'gainers',
      titleTop: "Top Gainers: ",
      title: "Top Stocks of the Day",
      info: "The 'Market Top Gainers' screener lists the stocks with the highest percentage gains in a single day.",
      exportFileName: 'market_gainers.csv',
      data: marketGainers,
      setData: setMarketGainers,
      loading: loadingGainers,
      setLoading: setLoadingGainers,
      endpoint: '/api/stock-price-change/top-movers?period=1D&type=gainers&limit=50&usOnly=true',
      renderPercentChange: (value) => (
        <div style={{ color: theme.palette.positive.main }}>
          +{parseFloat(value).toFixed(2)}%
        </div>
      ),
    },
    {
      id: 'losers',
      titleTop: "Biggest Losers: ",
      title: "Identify Top Declining Stocks of the Day",
      info: "The `Market Biggest Losers` screener provides a list of stocks that have experienced the largest percentage losses in value within a single trading day.",
      exportFileName: 'market_losers.csv',
      data: marketLosers,
      setData: setMarketLosers,
      loading: loadingLosers,
      setLoading: setLoadingLosers,
      endpoint: '/api/stock-price-change/top-movers?period=1D&type=losers&limit=50&usOnly=true',
      renderPercentChange: (value) => (
        <div style={{ color: theme.palette.negative.main }}>
          {parseFloat(value).toFixed(2)}%
        </div>
      ),
    },
    {
      id: 'actives',
      titleTop: "Most Active: ",
      title: "Discover High-Volume Stocks of the Day",
      info: "The `Market Most Active` screener provides a list of stocks with the highest trading volume in a single day.",
      exportFileName: 'market_actives.csv',
      data: marketActives,
      setData: setMarketActives,
      loading: loadingActives,
      setLoading: setLoadingActives,
      endpoint: '/api/stocks/marketactives',
      renderPercentChange: (value) => (
        <div style={{
          color: value > 0
            ? theme.palette.positive.main
            : value < 0
              ? theme.palette.negative.main
              : theme.palette.lightGray.main
        }}>
          {parseFloat(value).toFixed(2)}%
        </div>
      ),
    }
  ];

  // Handle main tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    updateUrlParams(newValue);
  };

  // Handle pre-market tab change
  const handlePreMarketTabChange = (event, newValue) => {
    setPreMarketTab(newValue);
    updateUrlParams(tabValue, 'premarket', newValue);
  };

  // Handle after-market tab change
  const handleAfterMarketTabChange = (event, newValue) => {
    setAfterMarketTab(newValue);
    updateUrlParams(tabValue, 'aftermarket', newValue);
  };

  // Handle high/low tab change
  const handleHighLowTabChange = (event, newValue) => {
    setHighLowTab(newValue);
    updateUrlParams(tabValue, 'highlow', newValue);
  };

  // Update tab values when URL changes
  useEffect(() => {
    const newTabValue = getInitialTabValue();
    if (newTabValue !== tabValue) {
      setTabValue(newTabValue);
    }

    const newPreMarketTab = getInitialSubTabValue('premarket');
    if (newPreMarketTab !== preMarketTab) {
      setPreMarketTab(newPreMarketTab);
    }

    const newAfterMarketTab = getInitialSubTabValue('aftermarket');
    if (newAfterMarketTab !== afterMarketTab) {
      setAfterMarketTab(newAfterMarketTab);
    }

    const newHighLowTab = getInitialSubTabValue('highlow');
    if (newHighLowTab !== highLowTab) {
      setHighLowTab(newHighLowTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch all data at once
  const fetchAllData = async () => {
    // Set all loading states to true
    setLoadingGainers(true);
    setLoadingLosers(true);
    setLoadingActives(true);
    setLoadingExtendedHours(true);
    setLoadingHighLow(true);

    try {
      // Make all API calls in parallel
      const [
        gainersResponse,
        losersResponse,
        activesResponse,
        preMarketResponse,
        afterMarketResponse,
        highLowResponse
      ] = await Promise.all([
        axios.get('/api/stock-price-change/top-movers?period=1D&type=gainers&limit=50&usOnly=true'),
        axios.get('/api/stock-price-change/top-movers?period=1D&type=losers&limit=50&usOnly=true'),
        axios.get('/api/stocks/marketactives'),
        axios.get('/api/extended-hours/movers/pre'),
        axios.get('/api/extended-hours/movers/after'),
        axios.get('/api/52-week-highs-lows')
      ]);

      // Update state with responses
      if (gainersResponse.data && gainersResponse.data.data) {
        setMarketGainers(gainersResponse.data.data);
      }

      if (losersResponse.data && losersResponse.data.data) {
        setMarketLosers(losersResponse.data.data);
      }

      if (activesResponse.data && activesResponse.data.data) {
        setMarketActives(activesResponse.data.data);
      }

      // Set extended hours data
      setExtendedHoursData({
        pre: {
          gainers: preMarketResponse.data?.data?.gainers || [],
          losers: preMarketResponse.data?.data?.losers || []
        },
        after: {
          gainers: afterMarketResponse.data?.data?.gainers || [],
          losers: afterMarketResponse.data?.data?.losers || []
        }
      });

      // Set 52-week high/low data
      setHighLowData({
        highs: highLowResponse.data?.data?.highs || [],
        lows: highLowResponse.data?.data?.lows || []
      });
    } catch (error) {
      console.error('Error fetching market data:', error);
    } finally {
      // Set all loading states to false
      setLoadingGainers(false);
      setLoadingLosers(false);
      setLoadingActives(false);
      setLoadingExtendedHours(false);
      setLoadingHighLow(false);
    }
  };

  // Fetch all data on component mount
  useEffect(() => {
    fetchAllData();
  }, []);

  // Regular market columns configuration
  const getColumns = (tabConfig) => [
    {
      field: 'symbol',
      headerName: 'Company',
      minWidth: 400,
      renderCell: (params) => (
        <div>
          <RouterLink
            to={`${baseUrl}/asset/${params.row.symbol}`}
            style={{ color: theme.palette.primary.main, textDecoration: 'none' }}
          >
            {params.row.symbol}
          </RouterLink>
          <div style={{ fontSize: '0.9em', color: '#888' }}>{params.row.name}</div>
        </div>
      ),
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'changesPercentage',
      headerName: '%Change',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => tabConfig.renderPercentChange(params.value),
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'price',
      headerName: 'Price',
      flex: 1,
      minWidth: 150,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'change',
      headerName: 'Change',
      flex: 1,
      minWidth: 150,
      align: 'right',
      headerAlign: 'right',
    },
  ];

  // Handle export for the current tab
  const handleExport = () => {
    const currentTab = tabsConfig[tabValue];
    exportToCsv(
      currentTab.exportFileName,
      getColumns(currentTab),
      currentTab.data
    );
  };

  // Determine overall loading state - only show loader on initial load
  const isInitialLoading = loadingGainers && loadingLosers && loadingActives && loadingExtendedHours && loadingHighLow;

  // Loading state for initial load only
  if (isInitialLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="table-container">
      <Container maxWidth="xl">
        {/* Main tabs navigation */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="market data tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="Top Gainers" {...a11yProps(0)} />
            <Tab label="Biggest Losers" {...a11yProps(1)} />
            <Tab label="Most Active" {...a11yProps(2)} />
            <Tab label="Pre-Market" {...a11yProps(3)} />
            <Tab label="After-Market" {...a11yProps(4)} />
            <Tab label="52-Week Highs/Lows" {...a11yProps(5)} />
          </Tabs>
        </Box>

        {/* Regular Market Tab panels */}
        {/* Gainers Tab Panel */}
        <TabPanel value={tabValue} index={0}>
          <MarketHoursGainers data={marketGainers} />
        </TabPanel>

        {/* Losers Tab Panel */}
        <TabPanel value={tabValue} index={1}>
          <MarketHoursLosers data={marketLosers} />
        </TabPanel>

        {/* Most Active Tab Panel */}
        <TabPanel value={tabValue} index={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexDirection={{ xs: 'column', sm: 'row' }}
            mb={2}
            gap={2}
          >
            <TitleAndInfo
              titleTop={tabsConfig[2].titleTop}
              title={tabsConfig[2].title}
              info={tabsConfig[2].info}
            />

            {user.plan.name === 'Premium' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
              >
                Export
              </Button>
            )}
          </Box>

          <DataGrid
            rows={tabsConfig[2].data}
            columns={getColumns(tabsConfig[2])}
            getRowId={(row) => row.symbol}
            disableColumnMenu
            pagination
            paginationPosition="middle"
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[5, 10, 25]}
            sx={{
              '& .MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 'bold',
              },
              '& .MuiDataGrid-columnHeaders': {
                fontSize: '0.8rem',
              },
            }}
          />
        </TabPanel>

        {/* Pre-Market Tab Panel */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ width: '100%' }}>
            <Tabs
              value={preMarketTab}
              onChange={handlePreMarketTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="pre-market tabs"
            >
              <Tab label="Pre-Market Gainers" />
              <Tab label="Pre-Market Losers" />
            </Tabs>

            {loadingExtendedHours ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Pre-Market Gainers */}
                {preMarketTab === 0 && (
                  <Box mt={3}>
                    <ExtendedHoursGainers
                      marketSession="pre"
                      data={extendedHoursData.pre.gainers}
                    />
                  </Box>
                )}

                {/* Pre-Market Losers */}
                {preMarketTab === 1 && (
                  <Box mt={3}>
                    <ExtendedHoursLosers
                      marketSession="pre"
                      data={extendedHoursData.pre.losers}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </TabPanel>

        {/* After-Market Tab Panel */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ width: '100%' }}>
            <Tabs
              value={afterMarketTab}
              onChange={handleAfterMarketTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="after-market tabs"
            >
              <Tab label="After-Market Gainers" />
              <Tab label="After-Market Losers" />
            </Tabs>

            {loadingExtendedHours ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* After-Market Gainers */}
                {afterMarketTab === 0 && (
                  <Box mt={3}>
                    <ExtendedHoursGainers
                      marketSession="after"
                      data={extendedHoursData.after.gainers}
                    />
                  </Box>
                )}

                {/* After-Market Losers */}
                {afterMarketTab === 1 && (
                  <Box mt={3}>
                    <ExtendedHoursLosers
                      marketSession="after"
                      data={extendedHoursData.after.losers}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </TabPanel>

        {/* 52-Week Highs/Lows Tab Panel */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ width: '100%' }}>
            <Tabs
              value={highLowTab}
              onChange={handleHighLowTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="52-week highs lows tabs"
            >
              <Tab label="52-Week Highs" />
              <Tab label="52-Week Lows" />
            </Tabs>

            {loadingHighLow ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* 52-Week Highs */}
                {highLowTab === 0 && (
                  <Box mt={3}>
                    <StockHighLowHighs
                      data={highLowData.highs}
                    />
                  </Box>
                )}

                {/* 52-Week Lows */}
                {highLowTab === 1 && (
                  <Box mt={3}>
                    <StockHighLowLows
                      data={highLowData.lows}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </TabPanel>
      </Container>
    </div>
  );
};

export default MarketMovers;